/* Native srcset owns selection. Measured slots also cover nested/variable carousels. */
(() => {
  'use strict';
  const widths = [160,240,320,400,480,600,750,900,1200,1500,1800,2400,3000,3840];
  function candidates(nativeWidth) {
    const max = Math.min(3840, Math.floor(Number(nativeWidth)));
    return max > 0 ? [...widths.filter(w => w < max), max] : [];
  }
  function urlAt(src, width) {
    try {
      const url = new URL(src, window.location.origin);
      const shopify = url.hostname === 'cdn.shopify.com' ||
        (url.origin === window.location.origin && url.pathname.startsWith('/cdn/shop/'));
      if (!shopify || !/\.(jpe?g|png|webp|avif)(?:$)/i.test(url.pathname)) return null;
      if (/_(?:\d+x\d*|x\d+)(?:_crop_\w+)?\./i.test(url.pathname)) return null;
      // Keep crop/height transformations untouched: changing width alone alters their framing.
      if (url.searchParams.has('height') || url.searchParams.has('crop')) return null;
      url.searchParams.set('width', String(width));
      return url.href;
    } catch { return null; }
  }
  function srcset(src, nativeWidth) {
    const list = candidates(nativeWidth);
    if (!list.length || !urlAt(src, list[0])) return '';
    return list.map(w => `${urlAt(src,w)} ${w}w`).join(', ');
  }
  function attributes(src, nativeWidth, sizes = 'auto, (min-width: 768px) 25vw, 50vw') {
    // Legacy records know their delivered width, not necessarily the original's width.
    // Do not invent original dimensions or increase an unknown-size source.
    let known = Number(nativeWidth);
    if (!(known > 0)) {
      try { known = Number(new URL(src, window.location.origin).searchParams.get('width')); } catch {}
    }
    const set = srcset(src, known);
    return set ? { srcset: set, sizes, 'data-pn-responsive': '' } : {};
  }
  function htmlAttributes(src, nativeWidth, sizes) {
    const escape = value => String(value).replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;');
    return Object.entries(attributes(src, nativeWidth, sizes)).map(([key,value]) => `${key}="${escape(value)}"`).join(' ');
  }
  function slot(width, height, ratio, cover) {
    return Math.ceil(cover ? Math.max(width, height * (Number(ratio) || 1)) : width);
  }
  const zooms = new WeakMap();
  async function zoom(img, scale) {
    if (img?.tagName === 'PICTURE') img = img.querySelector('img');
    if (!img || !(scale > 1)) return;
    const max = Number(img.dataset.pnNativeWidth);
    const desired = img.clientWidth * (window.devicePixelRatio || 1) * scale;
    const list = candidates(max);
    const target = list.find(w => w >= desired) || list.at(-1);
    const previous = zooms.get(img);
    if (!target || (previous && previous.width >= target)) return;
    const url = urlAt(img.getAttribute('src'), target);
    if (!url) return;
    const request = { width: target };
    zooms.set(img, request);
    const preload = new Image();
    const ready = new Promise(resolve => { preload.onload = () => resolve(true); preload.onerror = () => resolve(false); });
    preload.src = url;
    const ok = await ready;
    if (zooms.get(img) !== request) return;
    if (!ok) { zooms.delete(img); return; }
    // Only replace after the high-resolution image has loaded successfully.
    img.removeAttribute('srcset');
    img.src = url;
  }
  window.PianoImages = { candidates, urlAt, srcset, attributes, htmlAttributes, slot, zoom };
  const seen = new WeakSet();
  function prepare(img) {
    if (!img.hasAttribute('data-pn-dynamic')) return;
    const attrs = attributes(img.getAttribute('src'), img.dataset.pnNativeWidth, img.dataset.pnSizes);
    if (!attrs.srcset) { img.removeAttribute('srcset'); return; }
    Object.entries(attrs).forEach(([key,value]) => { if (img.getAttribute(key) !== value) img.setAttribute(key,value); });
  }
  function measure(img) {
    if (!img.isConnected || !img.clientWidth) return;
    const picture = img.closest('picture');
    const cover = picture?.hasAttribute('data-pn-cover') || getComputedStyle(img).objectFit === 'cover';
    const size = slot(img.clientWidth, img.clientHeight, img.dataset.pnRatio, cover);
    if (img.hasAttribute('srcset') && img.sizes !== `${size}px`) img.sizes = `${size}px`;
    picture?.querySelectorAll('source[data-pn-ratio]').forEach(source => {
      source.sizes = `${slot(img.clientWidth, img.clientHeight, source.dataset.pnRatio, cover)}px`;
    });
  }
  const resize = typeof ResizeObserver === 'function' ? new ResizeObserver(entries => entries.forEach(e => measure(e.target))) : null;
  function scan(root) {
    const selector = 'img[data-pn-responsive], img[data-pn-dynamic]';
    const images = [...(root.matches?.(selector) ? [root] : []), ...root.querySelectorAll(selector)];
    images.forEach(img => { prepare(img); if (seen.has(img)) return; seen.add(img); measure(img); resize?.observe(img); });
  }
  if (typeof document !== 'undefined') {
    scan(document);
    new MutationObserver(records => records.forEach(r => {
      if (r.type === 'attributes') { scan(r.target); measure(r.target); }
      r.addedNodes.forEach(node => { if (node.nodeType === 1) scan(node); });
      r.removedNodes.forEach(node => {
        if (node.nodeType !== 1) return;
        if (node.matches('img')) { resize?.unobserve(node); seen.delete(node); }
        node.querySelectorAll('img').forEach(img => { resize?.unobserve(img); seen.delete(img); });
      });
    })).observe(document.documentElement, { childList: true, subtree: true, attributes: true, attributeFilter: ['src', 'data-pn-responsive', 'data-pn-native-width'] });
    window.addEventListener('resize', () => document.querySelectorAll('img[data-pn-responsive]').forEach(measure), { passive: true });
  }
})();
